import { useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import _ from 'lodash';
import {
  AssetSymbol,
  BlockType,
  TradingStrategyOrCustom,
  AssetType,
  OptionType,
  Order,
  OrderAction,
  OrderLeg,
  OrderStatuses,
  TradingStrategyNameOrCustom,
  TradingStrategy,
  StrikeRules,
} from '@tradingblock/types';
import {
  AggregatedPositionDTO,
  AssetTypeToSymbolType,
  CustomStrategy,
  OrderBuilderType,
  OrderBuilder,
  OCCSymbolToOptionData,
  parseOccSymbol,
  deriveOptionExpirationType,
  Put,
  Call,
  Shares,
  getDefaultOrderQuantity,
  toAssetSymbol,
  toExpiration,
} from '@tradingblock/api';
import { useDispatcher } from '../../../data/global/hooks';
import { generateNewBlock } from '../../../utilities/block';
import { useFeedQuoteWithMetadata } from '../../../hooks/useFeedQuote';
import { OrderState } from '../../../blocks/Order/state/OrderState';
import {
  DefaultLegs,
  defaultStrategyForSymbol,
  DtoToOrderleg,
  getCustomDuplicateLegContractCount,
  getOrderCopyBuilderQuantity,
} from '../../../blocks/Order/OrderUtilities';
import { useStateSelector } from '../../../data/global/dataSelectors';
import { globalSettings } from '../../../data/global/selectors/settingSelectors';
import { BlockActions } from '../../../data/global/actions';
import { getType } from 'typesafe-actions';
import { Actions, OrderActions } from '../../../blocks/Order/state/OrderActions';
import { useBlockActionDispatchAndGenericActions } from '../BlockState';

interface UseTradeActionsProps {
  occSymbol: string | undefined;
  addBlockToGroupId?: string;
  data?: Partial<OrderState>;
  baseAssetSymbol: AssetSymbol;
  isUnderlyingSymbol?: boolean;
}
export const useOrderBlock = ({
  occSymbol,
  addBlockToGroupId,
  data,
  baseAssetSymbol,
  isUnderlyingSymbol,
}: UseTradeActionsProps) => {
  const { dispatcher } = useDispatcher();
  const globalOrderSettings = useStateSelector(globalSettings.order);

  const symbolQuote = useFeedQuoteWithMetadata(occSymbol || '');
  const symbol: AssetSymbol | undefined = useMemo(() => {
    if (symbolQuote) {
      // const { Symbol, Description, Exchange, UnderlyingSymbol, AssetClass } = symbolQuote;
      return baseAssetSymbol;
    }
    return baseAssetSymbol;
  }, [symbolQuote, baseAssetSymbol]);

  const createOrUpdateOrderBlock = useCallback(
    (addBlockAsNew?: boolean, useBlockDefaultStragety?: boolean) => {
      const initialBlockData = useBlockDefaultStragety
        ? {}
        : data || {
            legs: DefaultLegs({}, symbol, { optionType: undefined, strike: undefined, expiration: undefined }),
          };
      // const optionType = _.get(_.first(_.values(initialBlockData && initialBlockData.legs)), 'OptionType');
      // let strategy: TradingStrategyOrCustom = optionType ? (optionType === OptionType.Call ? Call : Put) : CustomStrategy;
      let strategy: TradingStrategyNameOrCustom = CustomStrategy.info.Name;
      const underlying = symbol.underlyingSymbol;
      if (isUnderlyingSymbol && underlying) {
        strategy = defaultStrategyForSymbol(underlying, undefined, globalOrderSettings);
      }
      // const quantity = _([defaultOrderQuantity, initialBlockData ? initialBlockData.quantity : undefined])
      //   .filter(v => v !== undefined)
      //   .min();
      const newBlock = generateNewBlock(BlockType.Order, {
        ...initialBlockData,
        symbol,
        strategy: strategy,
        quantity: getDefaultOrderQuantity(strategy, globalOrderSettings),
      });
      newBlock.groupId = addBlockToGroupId || '';
      // if adding block as new, don't pass "replace first match" options

      dispatcher.block.addOrUpdateBlock(
        newBlock,
        false,
        addBlockAsNew ? undefined : { blockType: BlockType.Order, groupId: addBlockToGroupId },
        useBlockDefaultStragety ? useBlockDefaultStragety : undefined
      );
    },
    [data, symbol, isUnderlyingSymbol, globalOrderSettings, addBlockToGroupId, dispatcher.block]
  );

  // if linkable block exists, show 2 trade options in dropdown
  return { createOrUpdateOrderBlock };
};

export interface ReplaceFirstMatchOptions {
  blockType: BlockType;
  groupId?: string;
}
export const useOrderBlockWithOrder = (
  build: (builder: OrderBuilderType) => OrderBuilderType,
  replaceFirstMatch?: ReplaceFirstMatchOptions
) => {
  const { dispatcher, dispatch } = useDispatcher();
  const globalOrderSettings = useStateSelector(globalSettings.order);
  const [{}, orderBlockDispatcher] = useBlockActionDispatchAndGenericActions<OrderState, OrderActions>();
  const orderBlockBuilder = useCallback(
    (addBlockAsNew?: boolean, linkGroup?: string) => {
      const orderBlockData = build(new OrderBuilder(globalOrderSettings).quantity(1)).order();
      const newBlock = generateNewBlock(BlockType.Order, orderBlockData);
      //dispatcher.block.addOrUpdateBlock(newBlock, false, addBlockAsNew === true ? undefined : replaceFirstMatch);

      if (linkGroup) newBlock.groupId = linkGroup;

      dispatch(
        BlockActions.addOrUpdateBlock(
          {
            block: newBlock,
            addedAt: dayjs().toDate(),
            preserveGroupOrderData: false,
            replaceFirstMatch: addBlockAsNew === true ? undefined : replaceFirstMatch,
          },
          { persist: true }
        )
      );

      orderBlockDispatcher({ type: getType(Actions.asyncValidateOrder), payload: {} });
    },
    [build, globalOrderSettings, dispatch, replaceFirstMatch, orderBlockDispatcher]
  );

  // if linkable block exists, show 2 trade options in dropdown
  return orderBlockBuilder;
};

export const getStrategyForLegs = (legs: OrderLeg[]) => {
  const firstLeg = _.first(legs);
  if (legs.length === 1 && firstLeg) {
    const sinfo = parseOccSymbol(firstLeg.OccSymbol || firstLeg.Symbol);
    if (sinfo.assetType === AssetType.Option) {
      return sinfo.option === OptionType.Put ? Put : Call;
    } else {
      return Shares;
    }
  }
  return CustomStrategy;
};

/** Net equity rows that share the same symbol (and subaccount) into one line so close orders use a single share leg. */
const consolidateEquityPositionsForClose = (rows: AggregatedPositionDTO[]): AggregatedPositionDTO[] => {
  const groups = _.groupBy(rows, p => {
    const sub = p.SubaccountId !== undefined && p.SubaccountId !== null ? String(p.SubaccountId) : '-1';
    return p.Symbol + '\0' + sub;
  });
  return _.compact(
    _.map(groups, group => {
      const netOpen = _.sumBy(group, g => g.OpenQuantity);
      if (netOpen === 0) {
        return undefined;
      }
      const first = group[0];
      return {
        ...first,
        OpenQuantity: netOpen,
        PositionIds: _.flatMap(group, g => g.PositionIds),
      } as AggregatedPositionDTO;
    })
  );
};

/** Build closing legs for every non-flat line in aggregated positions (equity and options). */
export const aggregatedOpenPositionsToCloseLegs = (positions: AggregatedPositionDTO[]): OrderLeg[] => {
  const nonZero = _.filter(positions, p => p.OpenQuantity !== 0);
  const equityRows = _.filter(nonZero, p => p.AssetType === AssetType.Equity);
  const optionRows = _.filter(nonZero, p => p.AssetType !== AssetType.Equity);
  const consolidatedEquity = consolidateEquityPositionsForClose(equityRows);
  const mergedPositions = consolidatedEquity.concat(optionRows);

  return mergedPositions.flatMap(position => {
    const symbol = position.Symbol;
    const assetType = position.AssetType;
    const underlyingSymbol = position.UnderlyingSymbol;
    const assetSymbol = toAssetSymbol(symbol, assetType, AssetTypeToSymbolType(assetType), {
      name: underlyingSymbol,
      underlyingSymbol,
      rootSymbol: underlyingSymbol,
    });
    const openQuantity = Math.abs(position.OpenQuantity);
    const action = position.OpenQuantity > 0 ? OrderAction.Sell : OrderAction.Buy;

    if (position.AssetType === AssetType.Equity) {
      const legsDto = DefaultLegs({}, assetSymbol, {
        assetType: AssetType.Equity,
        orderAction: action,
        quantity: openQuantity,
      });
      return Object.values(legsDto).map(l => ({
        ...DtoToOrderleg(l, assetSymbol),
        Quantity: openQuantity,
      }));
    }

    const optionData = OCCSymbolToOptionData(symbol);
    const optionExpirationType = deriveOptionExpirationType(assetSymbol.symbol, underlyingSymbol || '');
    const legsDto = DefaultLegs({}, assetSymbol, {
      assetType: AssetType.Option,
      optionType: optionData.option,
      orderAction: action,
      strike: optionData.strike,
      quantity: openQuantity,
      expiration: optionData.expiration
        ? toExpiration(optionData.expiration, assetSymbol.symbol, optionExpirationType)
        : undefined,
    });
    return Object.values(legsDto).map(l => ({
      ...DtoToOrderleg(l, assetSymbol),
      Quantity: openQuantity,
    }));
  });
};

export interface ApplyCloseLegsOptions {
  /** When set, used as order-level quantity for non-custom strategies (matches useCloseOrderAction callers). */
  explicitOrderQuantity?: number;
}

/**
 * Order blocks allow at most one share (equity) leg. Nets multiple equity close legs into a single leg.
 */
export const mergeEquityLegsToSingle = (legs: OrderLeg[]): OrderLeg[] => {
  const nonEquity: OrderLeg[] = [];
  const equity: OrderLeg[] = [];
  for (let i = 0; i < legs.length; i++) {
    const leg = legs[i];
    const info = parseOccSymbol(leg.Symbol);
    if (info.assetType === AssetType.Equity) {
      equity.push(leg);
    } else {
      nonEquity.push(leg);
    }
  }
  if (equity.length <= 1) {
    return legs;
  }

  const symbols = _.uniq(_.map(equity, e => e.Symbol));
  if (symbols.length > 1) {
    console.warn('[mergeEquityLegsToSingle] Multiple equity symbols in close legs; netting into one leg.', {
      symbols,
    });
  }

  let net = 0;
  for (let i = 0; i < equity.length; i++) {
    const leg = equity[i];
    const q =
      leg.Quantity !== undefined && leg.Quantity !== null
        ? Math.abs(leg.Quantity)
        : leg.LegFillQuantity !== undefined && leg.LegFillQuantity !== null
        ? Math.abs(leg.LegFillQuantity)
        : 0;
    if (leg.Action === OrderAction.Sell) {
      net += q;
    } else if (leg.Action === OrderAction.Buy) {
      net -= q;
    }
  }

  if (net === 0) {
    return nonEquity;
  }

  const template = equity[0];
  const absNet = Math.abs(net);
  const merged: OrderLeg = {
    ...template,
    Action: net > 0 ? OrderAction.Sell : OrderAction.Buy,
    Quantity: absNet,
    LegFillQuantity: absNet,
  };

  return [merged].concat(nonEquity);
};

/** Apply closing legs to an order builder (shared by row-level close and aggregated underlying close). */
export const applyCloseLegsToOrderBuilder = (
  builder: OrderBuilderType,
  symbol: AssetSymbol,
  legs: OrderLeg[],
  subaccountId: number = -1,
  options?: ApplyCloseLegsOptions
): OrderBuilderType => {
  const mergedLegs = mergeEquityLegsToSingle(legs);
  const strategy: TradingStrategyOrCustom = getStrategyForLegs(mergedLegs);
  const isCustom = strategy === CustomStrategy;
  const isSingleLeggedOrder = mergedLegs.length === 1;
  const firstLeg = _.first(mergedLegs);

  let orderQuantity = isCustom
    ? 1
    : options && options.explicitOrderQuantity !== undefined
    ? Math.abs(options.explicitOrderQuantity)
    : firstLeg && firstLeg.Quantity !== undefined
    ? Math.abs(firstLeg.Quantity)
    : 1;

  let orderAction: OrderAction = OrderAction.Buy;

  if (strategy.info.Name === 'Shares') {
    if (orderQuantity < 1) {
      orderQuantity = Math.ceil(orderQuantity);
    } else {
      orderQuantity = Math.floor(orderQuantity);
    }
  }

  if (isSingleLeggedOrder && firstLeg) {
    orderAction = firstLeg.Action;
  }

  const baseBuilder = builder
    .symbol(symbol)
    .strategy(strategy)
    .action(orderAction)
    .quantity(orderQuantity)
    .subaccountId(subaccountId);

  return _.reduce(
    mergedLegs,
    (b, l) => {
      const legQuantity = isCustom
        ? l.Quantity !== undefined
          ? Math.abs(l.Quantity)
          : options && options.explicitOrderQuantity !== undefined
          ? Math.abs(options.explicitOrderQuantity)
          : 1
        : 1;

      return b.leg(ll => {
        const symbolInfo = parseOccSymbol(l.Symbol);
        const buildRes = ll
          .assetType(symbolInfo.assetType)
          .action(l.Action)
          .quantity(legQuantity)
          .subaccountId(subaccountId);

        if (symbolInfo.assetType === AssetType.Equity) {
          return buildRes;
        }
        const legExpiration = (l as { Expiration?: { date: Date; optionExpirationType?: number } }).Expiration;
        const expirationDate = legExpiration && legExpiration.date ? legExpiration.date : symbolInfo.expiration;
        const optionExpirationType =
          legExpiration && legExpiration.optionExpirationType !== undefined
            ? legExpiration.optionExpirationType
            : deriveOptionExpirationType(l.OccSymbol || l.Symbol, symbol.symbol);
        return buildRes
          .optionType(symbolInfo.option || OptionType.Call)
          .expiration(expirationDate, optionExpirationType)
          .strike(symbolInfo.strike)
          .subaccountId(subaccountId);
      });
    },
    baseBuilder
  );
};

export const useCloseOrderAction = (
  order: Pick<Order, 'Quantity' | 'Legs' | 'OrderStatus'>,
  symbol: AssetSymbol,
  blockSelectionOptions?: Pick<ReplaceFirstMatchOptions, 'groupId'>,
  subaccountId?: number
) => {
  const { Quantity, Legs } = order;

  const buildCloseOrder = (builder: OrderBuilderType) => {
    return applyCloseLegsToOrderBuilder(builder, symbol, Legs, subaccountId ? subaccountId : -1, {
      explicitOrderQuantity: Math.abs(Quantity),
    });
  };

  const blockSelectionOpts = blockSelectionOptions || {};
  // if linkable block exists, show 2 trade options in dropdown
  const handleCloseOrder = useOrderBlockWithOrder(buildCloseOrder, {
    blockType: BlockType.Order,
    ...blockSelectionOpts,
  });
  return useMemo(() => handleCloseOrder, [handleCloseOrder]);
};

export const useTradeSymbolAction = (
  symbol: AssetSymbol,
  fn: (builder: OrderBuilderType) => OrderBuilderType,
  blockSelectionOptions?: Pick<ReplaceFirstMatchOptions, 'groupId'>
) => {
  const buildTradeOrder = (builder: OrderBuilderType) => {
    // const legs = DefaultLegs({}, assetSymbol, { assetType, optionType, orderAction: action, strike, expiration:  });
    const baseBuilder = builder
      .symbol(symbol)
      .strategy(CustomStrategy)
      .quantity(1);
    return fn(baseBuilder);
  };
  const blockSelectionOpts = blockSelectionOptions || {};
  // if linkable block exists, show 2 trade options in dropdown
  const handleTradeOrder = useOrderBlockWithOrder(buildTradeOrder, {
    blockType: BlockType.Order,
    ...blockSelectionOpts,
  });

  return useMemo(() => handleTradeOrder, [handleTradeOrder]);
};

export const useOrderCopyTradeAction = (
  order: Pick<Order, 'Quantity' | 'Legs' | 'OrderStatus'>,
  symbol: AssetSymbol,
  blockSelectionOptions?: Pick<ReplaceFirstMatchOptions, 'groupId'>
) => {
  const { Quantity, Legs, OrderStatus } = order;
  const strategy = getStrategyForLegs(Legs);
  const isCustom = strategy.info.Name === CustomStrategy.info.Name;
  const isSingleLeggedStrategyOrder = !isCustom && Legs.length === 1;
  const firstLeg = _.first(Legs);
  const orderCancelled = OrderStatus === OrderStatuses.Cancelled;

  const handleTradeOrder = (builder: OrderBuilderType) => {
    // const legs = DefaultLegs({}, assetSymbol, { assetType, optionType, orderAction: action, strike, expiration:  });

    let baseBuilder = builder;
    let quantity = 1;
    let orderAction = OrderAction.Buy;

    // if (firstLeg && (isSingleLeggedStrategyOrder || orderCancelled)) {
    //   quantity = Quantity;
    //   orderAction = firstLeg.Action === OrderAction.Buy ? OrderAction.Sell : OrderAction.Buy;
    // } else if (!isCustom && firstLeg) {
    //   quantity = Math.abs(firstLeg.LegFillQuantity || 1);
    //   orderAction = firstLeg.Action === OrderAction.Buy ? OrderAction.Sell : OrderAction.Buy;
    // }
    if (isSingleLeggedStrategyOrder && firstLeg) {
      quantity = Quantity;
      orderAction = firstLeg.Action === OrderAction.Buy ? OrderAction.Sell : OrderAction.Buy;
    }

    baseBuilder = baseBuilder
      .symbol(symbol)
      .action(orderAction)
      .strategy(strategy)
      .quantity(quantity);

    const tradeLegRatios = Legs.map(leg => leg.SpreadRatio);

    return _.reduce(
      Legs,
      (b, l) => {
        const { LegFillQuantity, SpreadRatio } = l;
        // const legQuantity = isCustom ? LegFillQuantity : 1;
        const legQuantity = isCustom
          ? getCustomDuplicateLegContractCount(Quantity, SpreadRatio, tradeLegRatios)
          : 1;

        return b.leg(ll => {
          const symbolInfo = parseOccSymbol(l.Symbol);
          const buildRes = ll
            .assetType(symbolInfo.assetType)
            .action(l.Action === OrderAction.Buy ? OrderAction.Sell : OrderAction.Buy)
            .quantity(legQuantity);

          if (symbolInfo.assetType === AssetType.Equity) {
            return buildRes;
          }
          const legExpiration = (l as { Expiration?: { date: Date; optionExpirationType?: number } }).Expiration;
          const expirationDate = legExpiration && legExpiration.date ? legExpiration.date : symbolInfo.expiration;
          const optionExpirationType =
            legExpiration && legExpiration.optionExpirationType !== undefined
              ? legExpiration.optionExpirationType
              : deriveOptionExpirationType(l.OccSymbol || l.Symbol, symbol.symbol);
          return buildRes
            .optionType(symbolInfo.option || OptionType.Call)
            .expiration(expirationDate, optionExpirationType)
            .strike(symbolInfo.strike);
        });
      },
      baseBuilder
    );
  };

  const blockSelectionOpts = blockSelectionOptions || {};
  // if linkable block exists, show 2 trade options in dropdown
  const handleTradeOrderBuilder = useOrderBlockWithOrder(handleTradeOrder, {
    blockType: BlockType.Order,
    ...blockSelectionOpts,
  });
  return useMemo(() => handleTradeOrderBuilder, [handleTradeOrderBuilder]);
};

export const useOrderCopyAction = (
  order: Order<OrderLeg>,
  symbol: AssetSymbol,
  blockSelectionOptions?: Pick<ReplaceFirstMatchOptions, 'groupId'>,
  matchingStrategy?: TradingStrategy,
  setCustom?: boolean,
  duplicateOrder?: boolean
) => {
  const { Quantity, Legs, OrderType, Action } = order;
  let strategy = setCustom ? CustomStrategy : getStrategyForLegs(Legs);
  const isCustom = strategy.info.Name === CustomStrategy.info.Name;
  const isSingleLeggedStrategyOrder = !isCustom && Legs.length === 1;
  const isSingleLeggedCustomOrder = isCustom && Legs.length === 1;
  const firstLeg = Legs[0];

  if (isCustom && matchingStrategy && matchingStrategy.info.Name) {
    strategy = matchingStrategy;
  }

  /**
   * Adjusts the order of legs in a strategy based on the defined strike rules.
   *
   * This function is designed to work with two-legged options strategies where
   * the order of strikes (increasing or decreasing) matters, such as in put/call
   * vertical spreads. It checks the strategy's strike rules and reorders the
   * legs of the strategy if they are not in the correct order according to the
   * rules.
   *
   * @param {Object[]} Legs - An array of leg objects in the strategy. Each leg object
   *                           should have a 'Strike' property.
   * @param {Object} strategyProfile - The profile object of the strategy, which should
   *                                   contain the 'Rules' related to strike ordering.
   *
   * @example
   * For a call vertical buy, Legs should be ordered with lower strike first:
   * If Legs are [{Strike: 110}, {Strike: 100}], they will be reordered to
   * [{Strike: 100}, {Strike: 110}]
   * adjustLegOrderForStrikeRules(Legs, matchingStrategy.profile);
   *
   * Notes:
   * - The function directly modifies the 'Legs' array.
   * - It only applies adjustments for strategies with exactly two legs.
   * - The function assumes 'strategyProfile' has a structure where 'Rules' is an object
   *   that contains a 'Strike' property with values like 'IncreasingOrder' or
   *   'DecreasingOrder'.
   * - If the 'Rules' object or 'Strike' property is not defined, or if the strategy
   *   does not conform to the expected structure (e.g., not a two-legged strategy),
   *   the function does nothing.
   */
  const adjustLegOrderForStrikeRules = (Legs: any[], strategyProfile: TradingStrategy['profile']) => {
    if (Legs.length !== 2) {
      return; // Only adjust for two-legged strategies
    }

    const strikeRule = strategyProfile && strategyProfile.Rules && strategyProfile.Rules.Strike;
    if (!strikeRule) {
      return; // No strike rule defined, exit early
    }

    const firstLegHigher = Legs[0].Strike > Legs[1].Strike;

    // Reverse Legs if the order is not aligned with the strike rule
    if (
      (strikeRule === StrikeRules.IncreasingOrder && firstLegHigher) ||
      (strikeRule === StrikeRules.DecreasingOrder && !firstLegHigher)
    ) {
      Legs.reverse();
    }
  };

  const handleTradeOrder = (builder: OrderBuilderType) => {
    let baseBuilder = builder;
    let orderAction = Action ? Action : OrderAction.Buy;

    if (isSingleLeggedStrategyOrder && firstLeg) {
      orderAction = firstLeg.Action === OrderAction.Buy ? OrderAction.Buy : OrderAction.Sell;
    }

    if ('profile' in strategy) {
      adjustLegOrderForStrikeRules(Legs, strategy.profile);
    }

    const calculatedQuantity = getOrderCopyBuilderQuantity({
      sourceOrderQuantity: Quantity,
      isSingleLeggedCustomOrder,
      isCustom,
      setCustom,
      duplicateOrder,
    });

    if (duplicateOrder && isCustom && !isSingleLeggedCustomOrder && Quantity > 1) {
      console.debug('[useOrderCopyAction] Multi-leg duplicate: order quantity set to 1; contracts come from leg SpreadRatios', {
        sourceOrderQuantity: Quantity,
      });
    }

    baseBuilder = baseBuilder
      .symbol(symbol)
      .orderType(OrderType)
      .action(orderAction)
      .strategy(strategy)
      .quantity(calculatedQuantity);

    const duplicateLegRatios = Legs.map(leg => leg.SpreadRatio);

    return _.reduce(
      Legs,
      (b, l) => {
        const { LegFillQuantity, SpreadRatio } = l;
        // let legQuantity = isCustom ? LegFillQuantity : 1;
        // if (orderCancelled && LegFillQuantity === 0) {
        //   legQuantity = (SpreadRatio || 1) * Quantity;
        // }
        // if the order is being built from the duplicate order action button, then we need to multiply spread ratio by the quantity to determine the leg quantity
        const legQuantity =
          isCustom && !duplicateOrder
            ? isSingleLeggedCustomOrder
              ? Quantity
              : SpreadRatio || 1
            : isCustom && duplicateOrder
            ? getCustomDuplicateLegContractCount(Quantity, SpreadRatio, duplicateLegRatios)
            : 1;
        return b.leg(ll => {
          const symbolInfo = parseOccSymbol(l.Symbol);
          const buildRes = ll
            .assetType(symbolInfo.assetType)
            .action(l.Action)
            .quantity(legQuantity);

          if (symbolInfo.assetType === AssetType.Equity) {
            return buildRes;
          }
          // Preserve original Expiration object when available - parseOccSymbol only returns a Date
          // and loses optionExpirationType (AM vs PM). Without it, expiration/strike dropdowns
          // won't match API data (keyed by expirationValue which includes optionExpirationType).
          // Legs from OrderStrategyMatch.createOrder include Expiration (from DtoToOrderleg spread)
          const legExpiration = (l as { Expiration?: { date: Date; optionExpirationType?: number } }).Expiration;
          const expirationDate = legExpiration && legExpiration.date ? legExpiration.date : symbolInfo.expiration;
          const optionExpirationType =
            legExpiration && legExpiration.optionExpirationType
              ? legExpiration.optionExpirationType
              : deriveOptionExpirationType(l.OccSymbol || l.Symbol, symbol.symbol);
          return buildRes
            .optionType(symbolInfo.option || OptionType.Call)
            .expiration(expirationDate, optionExpirationType)
            .strike(symbolInfo.strike);
        });
      },
      baseBuilder
    );
  };

  const blockSelectionOpts = blockSelectionOptions || {};
  // if linkable block exists, show 2 trade options in dropdown
  const handleTradeOrderBuilder = useOrderBlockWithOrder(handleTradeOrder, {
    blockType: BlockType.Order,
    ...blockSelectionOpts,
  });
  return useMemo(() => handleTradeOrderBuilder, [handleTradeOrderBuilder]);
};
